/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import BetweenAccountsConfirmationTab from '../BetweenAccountsConfirmationTab/BetweenAccountsConfirmationTab';
import { ReactComponent as SupportIcon } from 'assets/support.svg';
import i18nContext from 'components/i18n-context';
import AmountInput from 'components/common/AmountInput';
import { ContactUs } from 'components/common/ContactUs/ContactUs';
import { BACKEND_MODULE_TYPES, PAYMENT_PROVIDERS } from 'components/common/constants';
import Loader from 'components/common/Loader';
import PopUpSuccessScheme from 'components/common/PopUpScheme/PopUpSuccessScheme';
import PopUpInternalActionConfirmationScheme from 'components/common/PopUpScheme/PopUpInternalActionConfirmationScheme';
import { getAccountsOptionsForSelect } from 'components/utils';
import { ROUTE_PATHS } from 'routes/constants';
import { useMediaQuery } from 'services/useMediaQUery';
import {
  amountFormattedValue,
  findAccountByCurrency,
  getErrorMessageForAlert,
  isCryptoProvider,
  MEDIA_QUERY,
  prepareFieldErrors
} from 'services/utils';
import { CRYPTO_CURRENCY_PRECISION } from 'services/currenciesConstant';
import Alert from 'uikit/Alert/Alert';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { NewContainer } from 'uikit/NewContainer/NewContainer';
import { PopUp } from 'uikit/PopUp/PopUp';
import './BetweenAccountsPaymentForm.scss';

const BetweenAccountsPaymentForm = ({
  activeWallets,
  customerNumber,
  topUpAccount,
  betweenAccountsStore,
  userStore
}) => {
  const i18n = useContext(i18nContext);
  const isMobile = useMediaQuery(MEDIA_QUERY.MOBILE);
  const navigate = useNavigate();

  const getServerFieldsErrors = (variable) => prepareFieldErrors(i18n, betweenAccountsStore.error, variable);

  useEffect(() => {
    if (!betweenAccountsStore.isRepeatTransaction && !betweenAccountsStore.isRedirectFromPaymentForm) {
      betweenAccountsStore.resetStore();
      if (betweenAccountsStore.isCardAccountTopUp) {
        betweenAccountsStore.setIsRedirectToCards(true);
        betweenAccountsStore.setIsCardAccountTopUp(false);
      }
    } else if (betweenAccountsStore.isRepeatTransaction) {
      betweenAccountsStore.setIsRepeatTransactionStatus(false);
      validateForm(values);
    } else if (betweenAccountsStore.isRedirectFromPaymentForm) {
      betweenAccountsStore.setIsRedirectFromPaymentFormStatus(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter out shared Wallester accounts for representatives
  // Representatives cannot make transfers from shared Wallester wallets
  const filteredAccounts = activeWallets.filter((account) => {
    // If it's a Wallester account and user is not the owner - hide it
    return !(
      account.transfer_provider === PAYMENT_PROVIDERS.WALLESTER &&
      account.wallet_account_holder &&
      account.wallet_account_holder === customerNumber
    );
  });

  useEffect(() => {
    if (!betweenAccountsStore.isInitialized && filteredAccounts.length > 0) {
      if (topUpAccount) {
        handleChangeAccountTo('accountTo', topUpAccount?.wallet_number);
      } else {
        const accountFrom = findAccountByCurrency({ accounts: filteredAccounts });
        const accountTo = filteredAccounts.find(
          (account) =>
            account?.wallet_number !== accountFrom?.wallet_number && account?.currency === accountFrom?.currency
        );
        betweenAccountsStore.setFromToAccounts(accountFrom, accountTo);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredAccounts, betweenAccountsStore.isInitialized]);

  useEffect(() => {
    if (betweenAccountsStore.isInternalTransferCompleted) {
      userStore.getUserAccounts();
      betweenAccountsStore.setIsInternalTransferCompleted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [betweenAccountsStore.isInternalTransferCompleted]);

  const handleChangeAccountFrom = (name, value) => {
    const selectedAccount = filteredAccounts.find((account) => account?.wallet_number === value);
    const accountTo = filteredAccounts.find((account) =>
      betweenAccountsStore.accountTo?.wallet_number === selectedAccount?.wallet_number ||
      betweenAccountsStore.accountTo?.currency !== selectedAccount?.currency
        ? account?.wallet_number !== selectedAccount?.wallet_number && account?.currency === selectedAccount?.currency
        : account?.wallet_number === betweenAccountsStore.accountTo?.wallet_number
    );
    betweenAccountsStore.setFromToAccounts(selectedAccount, accountTo);
  };

  const handleChangeAccountTo = (name, value) => {
    const selectedAccount = filteredAccounts.find((account) => account?.wallet_number === value);
    const accountFrom = filteredAccounts.find((account) =>
      betweenAccountsStore.accountFrom?.wallet_number === selectedAccount?.wallet_number ||
      betweenAccountsStore.accountFrom?.currency !== selectedAccount?.currency
        ? account?.wallet_number !== selectedAccount?.wallet_number && account?.currency === selectedAccount?.currency
        : account?.wallet_number === betweenAccountsStore.accountFrom?.wallet_number
    );

    betweenAccountsStore.setFromToAccounts(accountFrom, selectedAccount);
  };

  const getAvailableBalance = (account) =>
    account &&
    i18n.getMessage('sendMoney.topLabel', {
      available: account.availableDetails?.displayAmount || amountFormattedValue(account.available, 2)
    });

  const accountsOptions = getAccountsOptionsForSelect(filteredAccounts);

  // The "no account in this currency" hint is a plain string; after a failed request the store holds an error
  // object instead, which must not be rendered inside the select (React throws → blank page).
  const missingAccountError = typeof betweenAccountsStore.error === 'string' ? betweenAccountsStore.error : '';

  const isBalanceExceeded = (amount, balance) => {
    return amount > balance;
  };

  const handleCheckInternalTransaction = async () => {
    if (betweenAccountsStore.amount && betweenAccountsStore.accountFrom && betweenAccountsStore.accountTo) {
      if (isBalanceExceeded(Number(betweenAccountsStore.amount), betweenAccountsStore?.accountFrom?.available)) {
        betweenAccountsStore.setError({ fields: { amount: 'balanceExceeded' } });
      } else {
        betweenAccountsStore.setError(null);
        await betweenAccountsStore?.checkInternalTransaction(customerNumber);
      }
    }
  };

  const handleVerifyInternalTransaction = async (securityCode) => {
    await betweenAccountsStore.verifyInternalTransaction(customerNumber, securityCode);
  };

  // Both accounts must be chosen: an account that has no pair in its currency leaves the other one empty, and the
  // transfer can't be checked, confirmed or created without it (TRN-7558).
  const areAccountsSelected = !!betweenAccountsStore.accountFrom && !!betweenAccountsStore.accountTo;

  const handleSubmit = async (values) => {
    if (!areAccountsSelected) return;

    betweenAccountsStore.setAmount(values.amount);
    betweenAccountsStore.setDescription(values.comment);
    await handleCheckInternalTransaction();
    if (!betweenAccountsStore.error) {
      betweenAccountsStore.setIsShowingConfirmationTab(true);
    }
  };

  const handleConfirm = async () => {
    await betweenAccountsStore.createInternalTransaction(customerNumber);
  };

  const handleCloseSuccessModal = () => {
    if (betweenAccountsStore.isRedirectToCards) {
      navigate(ROUTE_PATHS.CARDS);
    }
    betweenAccountsStore.resetStore();
    resetForm();
    betweenAccountsStore.setIsShowingConfirmationTab(false);
  };

  const handleValidateField = (fieldName) => {
    // Clears a stale server error, but not the "choose both accounts" one — that stays until both are chosen.
    if (areAccountsSelected) betweenAccountsStore.setError(null);
    if (fieldName === 'amount') {
      setFieldValue(fieldName, values.amount.replace(/\.+$/, ''));
    }
    validateField(fieldName);
  };

  const form = useFormik({
    validateOnChange: false,
    initialValues: {
      amount: betweenAccountsStore.isRepeatTransaction ? betweenAccountsStore.amount : '',
      comment:
        betweenAccountsStore.isRepeatTransaction && betweenAccountsStore.description
          ? betweenAccountsStore.description
          : ''
    },
    initialStatus: { amount: true, comment: true },
    validationSchema: Yup.object({
      amount: Yup.string()
        .required(i18n.getMessage('betweenAccounts.error.amountRequired'))
        .test('balanceExceeded', i18n.getMessage('error.fieldError.amount.balanceExceeded'), function () {
          const { accountFrom } = betweenAccountsStore;
          const { amount } = this.parent;
          return !(amount && accountFrom && isBalanceExceeded(Number(amount), accountFrom.available));
        }),
      comment: Yup.string().nullable(true).max(255, i18n.getMessage('betweenAccounts.error.descriptionMaxSize'))
    }),
    onSubmit: handleSubmit
  });

  const {
    values,
    errors,
    handleSubmit: formSubmit,
    handleChange,
    validateField,
    validateForm,
    isValid,
    setFieldValue,
    resetForm
  } = form;

  const handleCloseInternalActionConfirmation = () => {
    betweenAccountsStore.setIsShowingInternalActionConfirmation(false);
    betweenAccountsStore.setIsShowSwitchConfirmationMethodScreen(false);
  };

  const handleBackButtonClick = () => {
    betweenAccountsStore.setIsShowingConfirmationTab(false);
  };

  return (
    <>
      {activeWallets.length === 0 ? (
        <ContactUs
          icon={<SupportIcon />}
          header={i18n.getMessage('betweenAccounts.noAccounts.title')}
          description={i18n.getMessage('betweenAccounts.noAccounts.description')}
          button={i18n.getMessage('betweenAccounts.noAccounts.button')}
        />
      ) : (
        <section className={'page-container'}>
          <NewContainer
            className={'content-card-wrapper between-accounts-main-container'}
            header={
              betweenAccountsStore.isShowingConfirmationTab
                ? i18n.getMessage('betweenAccounts.reviewAndConfirm.container.label')
                : i18n.getMessage('betweenAccounts.paymentForm.container.label')
            }
            isBackButton={betweenAccountsStore.isShowingConfirmationTab}
            handleBackButtonClick={handleBackButtonClick}
          >
            {!betweenAccountsStore.isShowingConfirmationTab ? (
              <form className={'between-accounts-wrapper'} onSubmit={formSubmit}>
                <div className={'between-accounts-inputs-wrapper'}>
                  <InputDropDown
                    isRequired={true}
                    name={'accountFrom'}
                    bottomLabel={getAvailableBalance(betweenAccountsStore.accountFrom)}
                    value={betweenAccountsStore.accountFrom?.wallet_number}
                    currency={betweenAccountsStore.accountFrom?.currency}
                    networkCode={betweenAccountsStore.accountFrom?.network || null}
                    options={accountsOptions}
                    onChange={handleChangeAccountFrom}
                    label={i18n.getMessage('betweenAccounts.form.from')}
                    isMulti={false}
                    error={!betweenAccountsStore.accountFrom ? missingAccountError : ''}
                    isCryptoWallet={isCryptoProvider(betweenAccountsStore.accountFrom?.transfer_provider)}
                  />
                  <InputDropDown
                    isRequired={true}
                    name={'accountTo'}
                    bottomLabel={getAvailableBalance(betweenAccountsStore.accountTo)}
                    value={betweenAccountsStore.accountTo?.wallet_number}
                    currency={betweenAccountsStore.accountTo?.currency}
                    networkCode={betweenAccountsStore.accountTo?.network || null}
                    options={accountsOptions}
                    onChange={handleChangeAccountTo}
                    label={i18n.getMessage('betweenAccounts.form.to')}
                    isMulti={false}
                    error={!betweenAccountsStore.accountTo ? missingAccountError : ''}
                    isCryptoWallet={isCryptoProvider(betweenAccountsStore.accountTo?.transfer_provider)}
                  />
                  <AmountInput
                    isRequired={true}
                    label={i18n.getMessage('betweenAccounts.form.amount')}
                    name={'amount'}
                    value={values.amount}
                    onChange={handleChange}
                    onBlur={() => handleValidateField('amount')}
                    placeholder={i18n.getMessage('betweenAccounts.input.amount.placeholder')}
                    currency={betweenAccountsStore.accountFrom?.currency}
                    precision={betweenAccountsStore.accountFrom?.currency_precision}
                    error={
                      errors.amount ||
                      (betweenAccountsStore.exchangeRate?.min
                        ? getServerFieldsErrors(
                            betweenAccountsStore.exchangeRate?.minDetails?.displayAmount ||
                              amountFormattedValue(betweenAccountsStore.exchangeRate?.min)
                          )?.amount
                        : null)
                    }
                  />
                  <Input
                    type={'textarea'}
                    className={'between-accounts-description-input'}
                    name={'comment'}
                    label={i18n.getMessage('betweenAccounts.input.description.label')}
                    placeholder={i18n.getMessage('betweenAccounts.input.description.placeholder')}
                    rows={3}
                    value={values.comment}
                    onChange={handleChange}
                    onBlur={() => handleValidateField('comment')}
                    error={errors.comment || getServerFieldsErrors()?.comment}
                  />
                </div>

                {(betweenAccountsStore.temporaryError || (betweenAccountsStore.error && !missingAccountError)) && (
                  <Alert
                    type={'warning'}
                    className={'between-accounts-alert'}
                    message={
                      betweenAccountsStore.temporaryError
                        ? getErrorMessageForAlert(i18n, betweenAccountsStore.temporaryError)
                        : getErrorMessageForAlert(i18n, betweenAccountsStore.error)
                    }
                  />
                )}

                <div className={'between-accounts-action-wrapper'}>
                  <Button
                    type={'primary'}
                    roleType={'submit'}
                    size={'large'}
                    isDisabled={
                      !areAccountsSelected || !isValid || betweenAccountsStore.isLoading || betweenAccountsStore.error
                    }
                  >
                    {betweenAccountsStore.isLoading ? <Loader /> : i18n.getMessage('betweenAccounts.button.transfer')}
                  </Button>
                </div>
              </form>
            ) : (
              <BetweenAccountsConfirmationTab
                transferData={{
                  from: betweenAccountsStore.accountFrom,
                  to: betweenAccountsStore.accountTo,
                  amount: betweenAccountsStore.amount,
                  commission: betweenAccountsStore.commission,
                  totalAmount: betweenAccountsStore.totalAmount,
                  currency: betweenAccountsStore.accountFrom?.currency,
                  description: betweenAccountsStore.description
                }}
                error={betweenAccountsStore.temporaryError || betweenAccountsStore.error}
                onConfirm={handleConfirm}
                onEdit={handleBackButtonClick}
                isLoading={betweenAccountsStore.isLoading}
              />
            )}
          </NewContainer>
          <PopUp
            className={
              betweenAccountsStore.isShowingInternalActionConfirmation
                ? 'pop-up-card--transparent'
                : betweenAccountsStore.isShowSuccessPopUp
                  ? 'transaction-success'
                  : 'transaction-info'
            }
            show={betweenAccountsStore.isShowingInternalActionConfirmation || betweenAccountsStore.isShowSuccessPopUp}
            alignOnCenter={true}
            onClose={
              betweenAccountsStore.isShowSuccessPopUp ? handleCloseSuccessModal : handleCloseInternalActionConfirmation
            }
          >
            {betweenAccountsStore.isShowSuccessPopUp ? (
              <PopUpSuccessScheme
                onClose={handleCloseSuccessModal}
                message={i18n.getMessage('popUp.message.moneyTransfer')}
              />
            ) : (
              <PopUpInternalActionConfirmationScheme
                isLoading={betweenAccountsStore.isLoading}
                isShowSwitchConfirmationMethodScreen={betweenAccountsStore.isShowSwitchConfirmationMethodScreen}
                title={i18n.getMessage('betweenAccounts.popUp.actionConfirmation.title')}
                confirmButtonLabel={i18n.getMessage('betweenAccounts.popUp.actionConfirmation.button.confirm')}
                onClose={handleCloseInternalActionConfirmation}
                onConfirm={handleVerifyInternalTransaction}
                error={betweenAccountsStore.confirmationPopupError}
                clearError={() => betweenAccountsStore.clearConfirmationPopupError()}
                resendSecurityCode={() => betweenAccountsStore.resendTransactionSecurityCode()}
                setIsShowSwitchConfirmationMethodScreen={(status) =>
                  betweenAccountsStore.setIsShowSwitchConfirmationMethodScreen(status)
                }
                handleForceSwitchToAnotherConfirmationType={betweenAccountsStore.forceSwitchToAnotherConfirmationType}
                confirmationId={betweenAccountsStore.confirmationId}
                module={BACKEND_MODULE_TYPES.PAYMENT}
              />
            )}
          </PopUp>
        </section>
      )}
    </>
  );
};

BetweenAccountsPaymentForm.propTypes = {
  activeWallets: PropTypes.array,
  customerNumber: PropTypes.string,
  topUpAccount: PropTypes.object,
  betweenAccountsStore: MobXPropTypes.observableObject,
  userStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  activeWallets: stores.userStore?.activeWallets,
  customerNumber: stores.userStore.userData.account?.account_number,
  topUpAccount: stores.cardsStore.topUpAccount,
  betweenAccountsStore: stores.betweenAccountsStore,
  userStore: stores.userStore
}))(observer(BetweenAccountsPaymentForm));
